import useModPermsContracts from "@/features/mod-perms/hooks/useModPermsContracts.hook";
import type { ModPerms } from "@/features/mod-perms/api/get-all-mod-perms";
import { useModularPermissionsStore } from "@/features/mod-perms/stores/modular-permissions.store";
import { useQueryClient } from "@tanstack/react-query";
import {
  ModPermsQueryKeys,
  PermModOperation,
  type ModifiedPermissionsRequest,
  type RemoveSingleDelegateType,
} from "@/types/modular-permissions.types";
import { HexString } from "@/types/general.types";
import { useState } from "react";
import { useAccount, useSwitchChain } from "wagmi";
import { filterSocialAccounts } from "@/utils/filter-social-accounts.util";
import { prepareCalldata } from "@/utils/prepare-calldata.util";
import { useUIStore } from "@/stores/ui-states.store";
import { useRouter } from "next/navigation";
import { usePathname } from "next/navigation";
import { errorLog, log } from "@/features/bridge/utils/log";
import { BLESSNET_CHAIN_ID } from "@/features/bridge/constants/bridge.constant";

const useModularPermissionsManager = () => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    toggleOffPermission,
    toggleOnPermission,
    updatePermModRecord,
    delegatePermissions,
    clearDelegates,
    clearPermModRecords,
    removeDelegate,
    removePermModRecordsForDelegate,
    removeFromPendingCreations,
    clearPendingCreations,
    clearRecievedPermissions,
    removeReceivedPermission,
    pendingCreations,
    isDelegateContextGranted,
  } = useModularPermissionsStore();

  const { isSocialAccountBeingCreated } = useUIStore();

  const modPermsContract = useModPermsContracts();
  const connectedUserAccount = useAccount();
  const queryClient = useQueryClient();

  async function destroyDelegate(delegate: HexString) {
    // Start await animation
    // Add some state that animates the card
    // finish
    removeDelegate(delegate);
    removePermModRecordsForDelegate(delegate);
    removeFromPendingCreations(delegate);
  }

  function clearAllStates() {
    clearDelegates();
    clearPendingCreations();
    clearPermModRecords();
  }

  const account = useAccount();

  const pathname = usePathname()!;
  const router = useRouter();

  function handleCheckboxToggle(
    prevState: boolean,
    walletAddress: HexString,
    permissionId: number,
  ) {
    if (prevState) {
      toggleOffPermission(walletAddress, permissionId);
      updatePermModRecord(
        connectedUserAccount.address!,
        walletAddress,
        permissionId,
        PermModOperation.Remove,
      );
    } else {
      toggleOnPermission(walletAddress, permissionId);
      updatePermModRecord(
        connectedUserAccount.address!,
        walletAddress,
        permissionId,
        PermModOperation.Add,
      );
    }
  }

  const { switchChainAsync } = useSwitchChain({
    mutation: {
      onError: (error) => {
        errorLog(error);
      },
      onMutate: () => log("Switching network..."),
    },
  });

  async function ensureBlessnetNetwork(): Promise<boolean> {
    if (account?.chainId === BLESSNET_CHAIN_ID) return true;

    try {
      const switched = await switchChainAsync({ chainId: BLESSNET_CHAIN_ID });
      return Boolean(switched);
    } catch (err) {
      errorLog(err);
      return false;
    }
  }

  //! ============== Add chain request =========================

  // async function addCustomChain() {
  //   try {
  //     // Define the custom chain parameters
  //     const chainId = "0x1234"; // Example chain ID in hex
  //     const chainName = "My Custom Chain";
  //     const rpcUrls = ["https://my.custom.rpc.url"]; // RPC URL for the chain
  //     const nativeCurrency = {
  //       name: "CustomCoin",
  //       symbol: "CC", // 2-6 characters
  //       decimals: 18,
  //     };
  //     const blockExplorerUrls = ["https://explorer.my.custom.chain"];
  //     // Request to add the custom chain to the user's wallet
  //     await window.ethereum.request({
  //       method: "wallet_addEthereumChain",
  //       params: [
  //         {
  //           chainId,
  //           chainName,
  //           rpcUrls,
  //           nativeCurrency,
  //           blockExplorerUrls,
  //         },
  //       ],
  //     });
  //     console.log("Chain added successfully!");
  //   } catch (error) {
  //     console.error("Error adding chain:", error);
  //   }
  // }

  //! ============== Contract Call Functions ===================

  async function removeAllDelegations(
    callback?: (val: any) => void,
  ): Promise<void> {
    const queryKey = [
      ModPermsQueryKeys.ModularPermissions,
      connectedUserAccount.address,
      connectedUserAccount.chainId,
    ];
    const cachedData = queryClient.getQueryData(queryKey) as ModPerms | undefined;
    const hasServerPermissions = cachedData?.asVault?.length || 0; // Permissions in cache from initial API request
    const hasLocalPermissions = delegatePermissions.length; // Permissions in local state
    const hasPendingCreations = pendingCreations.length; //

    async function callContract() {
      try {
        await modPermsContract.removeAllDelegationsCall(
          connectedUserAccount.address!,
        );
      } catch (error) {
        console.error(error);
      } finally {
        clearAllStates();
        // Clear the cache of the removed values without triggering a refetch
        // (api latency will cause old data to show again if refetched)
        const cacheState = queryClient.getQueryData(queryKey) as ModPerms | undefined;
        if (cacheState) {
          queryClient.setQueryData(queryKey, {
            ...cacheState,
            asVault: [],
          } as ModPerms);
        }
        callback?.(false);
        return;
      }
    }

    // Do we have permissions that have been submitted but api hasnt updated yet?
    if (!hasServerPermissions && hasLocalPermissions && !hasPendingCreations) {
      callContract(); // Because we have actually submitted pending permissions
      return;
    }

    // Do we have permissions locally but also pending creations?
    if (!hasServerPermissions && hasLocalPermissions && hasPendingCreations) {
      // Check pending creations against local permissions to identify sent and unsent ones
      const isNoneSent = delegatePermissions.every((permission) =>
        pendingCreations.includes(permission.delegate),
      );
      if (isNoneSent) {
        // Simply clear local state
        clearAllStates();
        callback?.(false);
        return;
      } else {
        callContract();
        return;
      }
    }

    // Lastly if we have permissions from the server then we have to call contract anyway
    callContract();
  }

  async function removeAllReceivedDelegations(
    callback?: (val: any) => void,
  ): Promise<void> {
    if (!(await ensureBlessnetNetwork())) {
      // bail out if we couldn't switch
      return;
    }

    const cachedData = queryClient.getQueryData([
      ModPermsQueryKeys.ModularPermissions,
      connectedUserAccount.address,
      connectedUserAccount.chainId,
    ]) as ModPerms | undefined;
    const delegatesExist = !!(cachedData?.asDelegate?.length);
    if (!delegatesExist) return;

    try {
      await modPermsContract.removeAllReceivedDelegationsCall(
        connectedUserAccount.address!,
      );
      clearRecievedPermissions();
    } catch (error) {
      console.error(error);
    } finally {
      callback?.(false);
    }
  }

  async function removeDelegation(
    vault: HexString,
    delegate: HexString,
    destroyCallback?: (val?: any) => any,
  ) {
    if (!(await ensureBlessnetNetwork())) {
      // bail out if we couldn't switch
      return;
    }

    const isFreshlyCreated = pendingCreations.includes(delegate);

    if (isFreshlyCreated) {
      //? Remove it locally only
      destroyCallback?.();
      return;
    }

    try {
      const delegateToRemove: RemoveSingleDelegateType = {
        vault: vault,
        delegate: delegate,
      };
      await modPermsContract.removeDelegationCall(delegateToRemove);
      // Checks which delegation type is active then removes accordingly
      if (isDelegateContextGranted()) {
        destroyCallback?.();
      } else removeReceivedPermission(vault);
    } catch (error) {
      console.error(error);
    }
  }

  async function savePermissionChanges(
    permissionModifications: ModifiedPermissionsRequest[],
  ) {
    if (!(await ensureBlessnetNetwork())) {
      // bail out if we couldn't switch
      return;
    }

    //? Sort the permissions into social accounts and regular permissions
    const { regularPermissions, socialPermissions } =
      await filterSocialAccounts(permissionModifications);

    setIsLoading(true);

    try {
      //? Process the social accounts first
      if (socialPermissions.length) {
        const callData = await prepareCalldata(
          socialPermissions,
          account.address,
        );
        await modPermsContract.createSocialAccountWithPermissionsCall(callData);
      }

      //? Process the regular permissions
      if (regularPermissions.length) {
        await modPermsContract.modifyPermissionsCall(regularPermissions);
      }

      //? Clean the mod perm record state and pending creations state after sending
      clearPermModRecords();
      clearPendingCreations();
    } catch (error) {
      console.error("Error setting permissions:", error);
    } finally {
      setIsLoading(false);

      //? Clean the params from the URL after if the user is creating a social account
      if (isSocialAccountBeingCreated) router.replace(pathname);
    }
  }

  const contractClient = {
    isLoading,
    setIsLoading,
    removeDelegation,
    removeAllDelegations,
    removeAllReceivedDelegations,
    savePermissionChanges,
  };

  //! ============== Return values ===================

  return {
    handleCheckboxToggle,
    contractClient,
    actions: {
      destroyDelegate,
      removeReceivedPermission,
    },
  };
};

export default useModularPermissionsManager;
